import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { withRouter, RouteComponentProps, useLocation } from 'react-router';

import styled from 'styled-components';
import media from 'client/styles/media';
import { Container } from 'client/components/layout';
import { MutableThemeContext } from 'client/components/MutableThemeContext';

const Footer = styled.footer<{ hidden: boolean }>`
  width: 100%;
  background: ${({ theme }) => theme.color.footerBackground};
  border-top: 1px solid rgba(160, 174, 192, 0.4);
  margin-top: 100px;
  padding-top: 20px;
  padding-bottom: 25px;
  overflow: hidden;
  ${({ hidden }) => hidden && 'display: none;'}

  ${Container} {
    padding-left: 20px;
    padding-right: 20px;
  }

  a {
    display: inline-block;
    color: #52647a;
    text-decoration: none;
    &:hover,
    &:focus {
      color: #52647a;
      opacity: 0.8;
    }
  }
  ${media.sm} {
    padding-top: 15px;
  }
`;

const FooterTop = styled.div`
  display: flex;
  max-width: 320px;
  margin: 0 auto;
  @media (min-width: 380px) {
    padding-left: 30px;
  }
  ${media.sm} {
    max-width: none;
    justify-content: space-between;
    margin-bottom: 10px;
    padding-left: 0;
  }
`;
const FooterLinksColumn = styled.div`
  width: 40%;
  a {
    padding: 12px 0;
  }
  &:nth-child(1) {
    width: 60%;
  }
  ${media.sm} {
    padding: 0;
    div {
      display: inline-block;
    }
    &:nth-child(1) {
      div {
        margin-right: 24px;
      }
    }
    &:nth-child(2) {
      text-align: right;
      div {
        margin-left: 24px;
      }
    }
  }
`;

const FooterBottom = styled.div`
  ${media.sm} {
    display: flex;
    justify-content: space-between;
    align-items: flex-end;
  }
`;

const FooterLinksSocials = styled.div`
  text-align: center;
  margin: 0 -30px;
  padding: 15px 30px 0;
  border-top: 1px solid rgba(160, 174, 192, 0.4);
  color: #52647a;
  ${media.sm} {
    margin: 0;
    border-top: 0;
    padding: 15px 0 0;
    text-align: right;
    ${({ theme }) => theme.typography.small};
  }
`;

const AboutAuthors = styled.div`
  padding: 5px 0;
`;

const FooterLinksBottom = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 320px;
  margin: 0 auto;
  padding: 20px 0 30px;
  ${({ theme }) => theme.typography.small};
  a {
    padding: 5px 0;
  }
  div {
    width: 40%;
    &:nth-child(2n + 1) {
      width: 60%;
    }
  }
  @media (min-width: 380px) {
    padding-left: 30px;
  }
  ${media.sm} {
    display: block;
    max-width: none;
    margin: 0;
    padding: 0;
    div {
      padding: 0;
      width: auto;
      display: inline-block;
      margin-right: 16px;
      &:nth-child(2n + 1) {
        width: auto;
      }
    }
  }
`;

const SocialIcons = styled.div`
  margin-bottom: 10px;
  ${media.sm} {
    margin-bottom: 15px;
    margin-right: -10px;
  }
`;

const SocialIcon = styled.i`
  color: ${({ theme }) => theme.color.accent};
  font-size: 24px;
  margin: 10px;
`;

const HeartIcon = styled.i`
  color: ${({ theme }) => theme.color.brandRed};
  opacity: 0.5;
`;

const HeartThemeSwitch = () => {
  const { themeName, setThemeName } = useContext(MutableThemeContext);
  return (
    <HeartIcon
      className="fa fa-heart"
      onDoubleClick={() => setThemeName(themeName === 'Main' ? 'Experimental' : 'Main')}
    />
  );
};

const AppFooter = ({ hidden }: { hidden: boolean }) => (
  <Footer hidden={hidden}>
    <Container>
      <FooterTop>
        <FooterLinksColumn>
          <div>
            <Link to="/how-it-works">How it works</Link>
          </div>
          <div>
            <Link to="/event-organizers">For Organizers</Link>
          </div>
          <div>
            <Link to="/event-sponsors">For Sponsors</Link>
          </div>
          <div>
            <Link to="/communities-intro">For Communities</Link>
          </div>
        </FooterLinksColumn>
        <FooterLinksColumn>
          {/* <div>
            <Link to="/pricing">Pricing</Link>
          </div> */}
          <div>
            <Link to="/blog">Blog</Link>
          </div>
          <div>
            <Link to="/contact">Contact</Link>
          </div>
        </FooterLinksColumn>
      </FooterTop>

      <FooterBottom>
        <FooterLinksBottom>
          <div>
            <Link to="/community-guidelines">Community Guidelines</Link>
          </div>
          <div>
            <Link to="/terms-of-service">Terms of Service</Link>
          </div>
          <div>
            <Link to="/privacy-policy">Privacy policy</Link>
          </div>
          <div>
            <Link to="/cookie-policy">Cookie policy</Link>
          </div>
        </FooterLinksBottom>

        <FooterLinksSocials>
          <SocialIcons>
            <a className="non-text-link" href="https://www.facebook.com/Introwise/" aria-label="Facebook Page">
              <SocialIcon className="fa fa-facebook" aria-hidden="true" />
            </a>
            <a className="non-text-link" href="https://www.instagram.com/introwise/" aria-label="Instagram Profile">
              <SocialIcon className="fa fa-instagram" aria-hidden="true" />
            </a>
            <a className="non-text-link" href="https://medium.com/@introwise" aria-label="Medium Blog">
              <SocialIcon className="fa fa-medium" aria-hidden="true" />
            </a>
            <a className="non-text-link" href="https://twitter.com/Introwise" aria-label="Twitter Profile">
              <SocialIcon className="fa fa-twitter" aria-hidden="true" />
            </a>
          </SocialIcons>
          <AboutAuthors>
            Built with&nbsp;
            <HeartThemeSwitch />
            &nbsp;in Tartu, Estonia
          </AboutAuthors>
        </FooterLinksSocials>
      </FooterBottom>
    </Container>
  </Footer>
);

const AppFooterContainer = () => {
  // TODO: footer should appear on incoming invite page.
  // Currently matchmaking circle and incoming invite have same URL
  const { pathname } = useLocation();
  const unmounted = false; // pathname === '/';
  const hidden =
    pathname.match(/\/(?:events|communities)\/([\w-]*)\/circles|meetings\/(\w*)/g) ||
    pathname.match(/\/(?:events)\/([\w-]*)\/joinnew/g);
  return !unmounted && <AppFooter hidden={!!hidden} />;
};

export default AppFooterContainer;
